// Formatting shared by the three objective tabs. Kept pure and free of React so
// the rules ("no denominator" vs "measured 0%") can be unit-tested directly.

export const EMPTY = '—';

/**
 * The API sends null when a ratio has no denominator — nothing was in scope.
 * That is not 0%: rendering it as 0% would read as a failed target when in
 * fact nothing was measured.
 */
export const pct = (ratio, decimals = 0) => {
  if (ratio === null || ratio === undefined) return EMPTY;
  return `${(Number(ratio) * 100).toFixed(decimals)}%`;
};

export const RATIO_TONES = {
  good:    '#15803d',
  warning: '#b45309',
  bad:     '#b91c1c',
  none:    '#94a3b8',
  neutral: '#1e293b',
};

/**
 * Graded colour, for metrics the department actually committed to: 90% is the
 * bar the year-end sheet holds them to.
 *
 * Do NOT grade "meet CSD schedule" with it. That one measures how often the
 * date the customer asked for was achievable at all — it runs at 13-20% in
 * practice, so a 90% scale paints every single row red and the colour stops
 * carrying information. Use `RATIO_TONES.neutral` there and let the bar length
 * do the talking.
 */
export const ratioTone = (ratio) => {
  if (ratio === null || ratio === undefined) return RATIO_TONES.none;
  if (ratio >= 0.9) return RATIO_TONES.good;
  if (ratio >= 0.7) return RATIO_TONES.warning;
  return RATIO_TONES.bad;
};

/**
 * One palette for every segmented bar on the three tabs, so "green = made it,
 * red = missed it, grey = not judged yet" means the same thing everywhere.
 */
export const SEGMENT_COLORS = {
  met:      '#10b981',
  delayed:  '#f43f5e',
  pending:  '#cbd5e1',
  unknown:  '#fbbf24',
  primary:  '#3b82f6',
  neutral:  '#94a3b8',
};

/** Share of `total`, clamped to [0,1]; 0 when there is no denominator. */
export const share = (value, total) => {
  const denominator = Number(total) || 0;
  if (denominator <= 0) return 0;
  return Math.min(1, Math.max(0, Number(value || 0) / denominator));
};

export const fmtCount = (value) => (value === null || value === undefined ? EMPTY : Number(value).toLocaleString());

/**
 * Signed weight delta. Zero stays unsigned; positive/negative values carry the
 * direction so the grid can color them consistently.
 */
export const fmtDelta = (value, decimals = 2) => {
  if (value === null || value === undefined) return EMPTY;
  const num = Number(value);
  if (num === 0) return '0';
  const formatted = Math.abs(num).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${num > 0 ? '+' : '−'}${formatted}`;
};
